/**
 * Simplify error messages for user display
 * Converts verbose JSON error responses into readable messages
 */
function simplifyErrorMessage(error: string): string {
  // Extract the core error message from nested JSON
  // e.g., '{"error":"Failed...","message":"Gemini...{\"error\":\"...\"}"}' -> "Image generation failed. Please try again."

  // Check for common error patterns and provide friendly messages
  if (error.includes('personGeneration') || error.includes('generation_config')) {
    return "Image generation configuration error. Please try again.";
  }
  if (error.includes('Gemini') && error.includes('400')) {
    return "Image generation failed due to a configuration issue. Please try again.";
  }
  if (error.includes('Gemini') && error.includes('overlay')) {
    return "Failed to add title to cover image. Please try again.";
  }
  if (error.includes('Failed to generate')) {
    return "Image generation failed. Please try again.";
  }
  if (error.includes('timeout') || error.includes('Timeout')) {
    return "Request timed out. Please try again.";
  }

  // If it looks like JSON, try to extract a simple message
  if (error.startsWith('{') || error.includes('{"')) {
    try {
      // Try to parse and get the top-level error/message
      const parsed = JSON.parse(error);
      if (parsed.message && typeof parsed.message === 'string' && parsed.message.length < 100) {
        return parsed.message;
      }
      if (parsed.error && typeof parsed.error === 'string' && parsed.error.length < 100) {
        return parsed.error;
      }
    } catch {
      // Not valid JSON, continue
    }
  }

  // Truncate very long messages
  if (error.length > 150) {
    // Try to find a meaningful prefix before truncating
    const colonIndex = error.indexOf(':');
    if (colonIndex > 0 && colonIndex < 50) {
      return error.substring(0, colonIndex + 1) + " Please try again.";
    }
    return error.substring(0, 100) + "... Please try again.";
  }

  return error;
}

export const fetchWithToast = async (url: string, options: RequestInit, toast: any) => {
  try {
    console.log(`Fetching ${url} with method: ${options.method}...`);

    // Add Content-Type header if not present
    const fetchOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      }
    };

    try {
      const response = await fetch(url, fetchOptions);
      console.log(`Fetch response from ${url}: status=${response.status}`);

      if (!response.ok) {
        let errorText = "";
        try {
          const errorData = await response.json();
          errorText = errorData.error || `HTTP error! status: ${response.status}`;
        } catch (parseError) {
          errorText = `HTTP error! status: ${response.status}`;
        }

        console.error(`Fetch error from ${url}:`, errorText);

        // Simplify the error message for display
        const displayError = simplifyErrorMessage(errorText);

        // Handle 504 Gateway Timeout specifically
        if (response.status === 504) {
          toast({
            title: "Request Timeout",
            description: "The request is taking longer than expected. This can happen with complex stories or during high traffic. Please try again with a simpler request.",
            variant: "destructive",
            duration: 8000
          });
        } else {
          toast({
            title: "Error",
            description: displayError,
            variant: "destructive",
          });
        }

        return { error: errorText };
      }
      
      const responseData = await response.json();
      console.log(`Fetch from ${url} completed successfully`);
      return responseData;
    } catch (fetchError: any) {
      console.error(`Fetch error details for ${url}:`, {
        name: fetchError.name,
        message: fetchError.message,
        stack: fetchError.stack
      });
      
      toast({
        title: "Error",
        description: `Fetch error: ${fetchError.message}`,
        variant: "destructive",
      });
      
      return { error: `Fetch error: ${fetchError.message}` };
    }
  } catch (error: any) {
    console.error("Critical error in fetchWithToast:", error);
    toast({
      title: "Error",
      description: `Unexpected error: ${error.message}`,
      variant: "destructive",
    });
    return { error: `Unexpected error: ${error.message}` };
  }
};